import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { leaveApi } from '../api/leave.js';
import { ApiError } from '../api/client.js';
import { Avatar } from '../components/Avatar.jsx';

export default function AdminLeaveQueuePage() {
    const queryClient = useQueryClient();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['leave', 'pending'],
        queryFn: () => leaveApi.listPending(),
    });

    const [rejectDialog, setRejectDialog] = useState(null);
    const [actionMsg, setActionMsg] = useState(null);
    const [busy, setBusy] = useState(null);

    const refresh = () => queryClient.invalidateQueries({ queryKey: ['leave', 'pending'] });

    const handleApprove = async (req) => {
        setBusy(req.id);
        try {
            await leaveApi.approve(req.id);
            setActionMsg({ tone: 'success', text: `Approved ${req.employee.name}'s leave request.` });
            refresh();
        } catch (err) {
            setActionMsg({ tone: 'error', text: err.message || 'Approve failed.' });
        } finally {
            setBusy(null);
        }
    };

    return (
        <>
            <header className="flex items-center justify-between" style={{ marginBottom: 16 }}>
                <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>Leave queue</h1>
                <button onClick={refresh} className="btn btn-soft">Refresh</button>
            </header>

            {actionMsg && (
                <div
                    style={{
                        marginBottom: 14,
                        padding: '8px 12px',
                        fontSize: 13,
                        borderRadius: 'var(--border-radius-md)',
                        ...(actionMsg.tone === 'success'
                            ? {
                                background: 'var(--color-pill-approved-bg)',
                                color: 'var(--color-pill-approved-fg)',
                                border: '0.5px solid rgba(8, 80, 65, 0.18)',
                            }
                            : {
                                background: 'var(--color-pill-rejected-bg)',
                                color: 'var(--color-danger)',
                                border: '0.5px solid var(--color-danger-border)',
                            }),
                    }}
                >
                    {actionMsg.text}
                    <button
                        onClick={() => setActionMsg(null)}
                        style={{
                            marginLeft: 12,
                            fontSize: 11,
                            textDecoration: 'underline',
                            color: 'inherit',
                            background: 'transparent',
                            cursor: 'pointer',
                            border: 'none',
                        }}
                    >
                        dismiss
                    </button>
                </div>
            )}

            {isLoading && <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading…</p>}
            {isError && <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>Could not load requests.</p>}

            {data && data.data.length === 0 && (
                <div
                    className="surface-card"
                    style={{ padding: 40, textAlign: 'center' }}
                >
                    <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                        No pending leave requests in your scope.
                    </p>
                </div>
            )}

            {data && data.data.length > 0 && (
                <div className="flex flex-col" style={{ gap: 10 }}>
                    {data.data.map((req) => (
                        <RequestCard
                            key={req.id}
                            req={req}
                            busy={busy === req.id}
                            onApprove={() => handleApprove(req)}
                            onReject={() => setRejectDialog(req)}
                        />
                    ))}
                </div>
            )}

            {rejectDialog && (
                <RejectDialog
                    req={rejectDialog}
                    onCancel={() => setRejectDialog(null)}
                    onSuccess={() => {
                        const empName = rejectDialog.employee.name;
                        setRejectDialog(null);
                        setActionMsg({ tone: 'success', text: `Rejected ${empName}'s leave request.` });
                        refresh();
                    }}
                />
            )}
        </>
    );
}

function RequestCard({ req, busy, onApprove, onReject }) {
    return (
        <div className="surface-card flex items-start" style={{ padding: 14, gap: 12 }}>
            <Avatar user={req.employee} size="md" />
            <div className="flex-1 min-w-0">
                <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{req.employee.name}</p>
                <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                    {req.employee.department} · {req.employee.email}
                </p>
                <div
                    className="flex flex-wrap items-center"
                    style={{ marginTop: 8, gap: '4px 16px', fontSize: 13 }}
                >
                    <span>
                        <strong style={{ textTransform: 'capitalize' }}>{req.leave_type}</strong>
                        {' · '}
                        {formatRange(req.start_date, req.end_date)}
                    </span>
                    <span style={{ color: 'var(--color-text-secondary)' }}>
                        {req.working_days} {req.working_days === 1 ? 'day' : 'days'}
                    </span>
                    {req.is_half_day && (
                        <span className="pill pill-pending">
                            Half-day ({req.half_day_period})
                        </span>
                    )}
                </div>
                {req.reason && req.reason !== '(none provided)' && (
                    <p
                        style={{
                            fontSize: 12,
                            color: 'var(--color-text-secondary)',
                            marginTop: 8,
                            fontStyle: 'italic',
                            background: 'var(--color-bg-secondary)',
                            padding: '6px 10px',
                            borderRadius: 'var(--border-radius-md)',
                        }}
                    >
                        “{req.reason}”
                    </p>
                )}
                <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 6 }}>
                    Submitted {formatRelative(req.submitted_at)}
                </p>
            </div>
            <div className="flex flex-col" style={{ gap: 6 }}>
                <button onClick={onApprove} disabled={busy} className="btn btn-primary">
                    {busy ? '…' : 'Approve'}
                </button>
                <button onClick={onReject} disabled={busy} className="btn btn-danger">
                    Reject
                </button>
            </div>
        </div>
    );
}

function RejectDialog({ req, onCancel, onSuccess }) {
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (note.trim().length < 5) {
            setError('Please provide at least 5 characters of reason.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await leaveApi.reject(req.id, { note: note.trim() });
            onSuccess();
        } catch (err) {
            if (err instanceof ApiError) setError(err.message);
            else setError('Reject failed.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                zIndex: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <form
                onSubmit={onSubmit}
                className="surface-card"
                style={{ padding: 22, width: '100%', maxWidth: 460 }}
            >
                <h2 style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>
                    Reject {req.employee.name}'s request?
                </h2>
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 14 }}>
                    {capitalise(req.leave_type)} · {formatRange(req.start_date, req.end_date)}
                </p>
                <label
                    style={{
                        display: 'block',
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'var(--color-text-secondary)',
                        marginBottom: 6,
                    }}
                >
                    Reason for rejection (required, minimum 5 characters)
                </label>
                <textarea
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Conflicts with project deadline. Please reschedule for the following week."
                    className="textarea"
                    maxLength={1000}
                    autoFocus
                />
                <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                    {req.employee.name} will see this reason.
                </p>
                {error && (
                    <p
                        style={{
                            fontSize: 13,
                            color: 'var(--color-danger)',
                            marginTop: 8,
                            background: 'var(--color-pill-rejected-bg)',
                            border: '0.5px solid var(--color-danger-border)',
                            borderRadius: 'var(--border-radius-md)',
                            padding: '8px 12px',
                        }}
                    >
                        {error}
                    </p>
                )}
                <div
                    className="flex justify-end"
                    style={{
                        gap: 8,
                        paddingTop: 12,
                        marginTop: 12,
                        borderTop: '0.5px solid var(--color-border-tertiary)',
                    }}
                >
                    <button type="button" onClick={onCancel} className="btn btn-ghost">
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={submitting || note.trim().length < 5}
                        className="btn btn-danger"
                    >
                        {submitting ? 'Rejecting…' : 'Reject request'}
                    </button>
                </div>
            </form>
        </div>
    );
}

function formatRange(start, end) {
    if (start === end) return formatDate(start);
    return `${formatDate(start)} – ${formatDate(end)}`;
}

function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatRelative(ts) {
    if (!ts) return '';
    const ago = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(ago / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function capitalise(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}
